import React, { FC, useEffect, useState } from 'react'
import type { CrewClaim } from '../core/_requests'
import { getClaimMasterRemarks } from '../core/claimMasterReview'

type Props = {
  visible: boolean
  record: CrewClaim | null
  onClose: () => void
  onSave: (masterRemarks: string) => void
  onApprove: (masterRemarks: string) => void
  isLoading?: boolean
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const CrewClaimMasterReviewModal: FC<Props> = ({
  visible,
  record,
  onClose,
  onSave,
  onApprove,
  isLoading = false,
}) => {
  const [masterRemarks, setMasterRemarks] = useState('')
  const [remarksError, setRemarksError] = useState('')

  useEffect(() => {
    if (!visible || !record) return
    setMasterRemarks(getClaimMasterRemarks(record.remarks))
    setRemarksError('')
  }, [visible, record])


  const validateRemarks = () => {
    if (!masterRemarks || !masterRemarks.trim()) {
      setRemarksError('Master remarks are required.')
      return false
    }
    setRemarksError('')
    return true
  }

  if (!visible || !record) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Master Review - Crew Claim">
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '70vw',
          maxWidth: '900px',
          maxHeight: '80vh',
        }}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
          <div>
            <h5 className='modal-title text-dark m-0'>Master Review - Crew Claim</h5>
            <div className='text-muted small mt-1'>
              {record.crewName || '-'} | {record.rankName || '-'} | {record.vesselName || '-'}
            </div>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <span className='badge badge-light-warning'>Pending Master Review</span>
            <button type='button' className='btn-close m-0' onClick={onClose} disabled={isLoading} />
          </div>
        </div>

        <div className='flex-grow-1 overflow-auto px-5 py-4'>
          <div className='mb-4'>
            <label className={LABEL}>
              Master Remarks <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <textarea
              className='form-control'
              rows={4}
              value={masterRemarks}
              onChange={(event) => {
                setMasterRemarks(event.target.value)
                if (remarksError) setRemarksError('')
              }}
              placeholder='Add master review remarks'
              disabled={isLoading}
            />
            {remarksError && <div className='text-danger small mt-2'>{remarksError}</div>}
          </div>
        </div>

        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
          <button type='button' className='btn btn-light btn-sm' onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-light-primary btn-sm'
            disabled={isLoading}
            onClick={() => {
              if (!validateRemarks()) return
              onSave(masterRemarks)
            }}
          >
            Save Review Only
          </button>
          <button
            type='button'
            className='btn btn-success btn-sm'
            disabled={isLoading}
            onClick={() => {
              if (!validateRemarks()) return
              onApprove(masterRemarks)
            }}
          >
            Approve & Send to Office
          </button>
        </div>
      </div>
    </div>
  )
}

export default CrewClaimMasterReviewModal
